"use client";

import { cva } from "class-variance-authority";
import { ArrowLeft, ArrowRight } from "lucide-react";
import type { ButtonHTMLAttributes, ElementType, FC, HTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "@/components/ui/link";
import { cn } from "@/lib/utils";
import type { Layout } from "@/types";
import {
  PaginationContext,
  type PaginationContextValue,
  usePagination,
} from "./pagination.context";

export type PaginationLayout = Extract<Layout, "desktop" | "mobile">;

export interface PaginationProps extends HTMLAttributes<HTMLElement> {
  layout?: PaginationLayout;
  /** Normalizada: inteiro entre 1 e `totalPages`; valores inválidos (NaN, 0, negativo) viram 1. */
  currentPage: number;
  /** Sem ele o total é desconhecido. `0` (lista vazia) não renderiza nada. */
  totalPages?: number;
  /** Só vale sem `totalPages`: `false` esconde `Next` (paginação por cursor). */
  hasNextPage?: boolean;
  onPageChange?: (page: number) => void;
  as?: ElementType;
}

const ROOT_VARIANTS = cva("grid grid-cols-[1fr_auto_1fr] items-center", {
  variants: { layout: { desktop: "gap-3", mobile: "gap-2" } },
});

const Root: FC<PaginationProps> = ({
  layout = "desktop",
  currentPage,
  totalPages,
  hasNextPage,
  onPageChange,
  as: Tag = "nav",
  className,
  children,
  ...props
}) => {
  const total =
    totalPages !== undefined && Number.isFinite(totalPages)
      ? Math.max(0, Math.trunc(totalPages))
      : undefined;

  if (total === 0) return null;

  const page = Math.min(Math.max(1, Math.trunc(currentPage) || 1), total ?? Infinity);
  const contextValue: PaginationContextValue = {
    layout,
    currentPage: page,
    totalPages: total,
    hasNextPage,
    onPageChange,
  };

  return (
    <PaginationContext.Provider value={contextValue}>
      <Tag data-layout={layout} className={cn(ROOT_VARIANTS({ layout }), className)} {...props}>
        {children}
      </Tag>
    </PaginationContext.Provider>
  );
};

interface PartProps {
  className?: string;
  children?: ReactNode;
}

interface LinkStepProps
  extends Omit<LinkProps, "variant" | "href" | "className" | "children">,
    PartProps {
  href: string;
}

interface ButtonStepProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children" | "type">,
    PartProps {
  href?: undefined;
}

/** Com `href` vira link (props de `Link`); sem `href`, botão (props de `<button>`). */
export type StepProps = LinkStepProps | ButtonStepProps;

const STEP_VARIANTS = cva(
  "inline-flex shrink-0 items-center whitespace-nowrap rounded-pill font-semibold no-underline transition-colors hover:no-underline",
  {
    variants: {
      layout: {
        desktop: "gap-2 px-5 py-2.5 text-body-sm",
        mobile: "gap-1.5 px-3 py-1.5 text-body-xs",
      },
    },
  },
);

const ICON_VARIANTS = cva("", {
  variants: { layout: { desktop: "h-4 w-4", mobile: "h-3.5 w-3.5" } },
});

const PREVIOUS_CLASSES = "border border-border-default text-fg-strong hover:bg-bg-secondary";
const NEXT_CLASSES = "bg-primary text-on-primary hover:bg-primary-hover";

type StepControlProps = StepProps & { defaultRel: "prev" | "next"; onSelect: () => void };

const StepControl: FC<StepControlProps> = ({
  href,
  defaultRel,
  onSelect,
  className,
  children,
  ...rest
}) => {
  if (href !== undefined) {
    return (
      <Link
        rel={defaultRel}
        {...(rest as object)}
        href={href}
        variant="secondary"
        className={className}
      >
        {children}
      </Link>
    );
  }

  const { onClick, ...buttonProps } = rest as ButtonHTMLAttributes<HTMLButtonElement>;

  return (
    <button
      {...buttonProps}
      type="button"
      className={className}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onSelect();
      }}
    >
      {children}
    </button>
  );
};

const Previous: FC<StepProps> = ({ className, children, ...props }) => {
  const { layout, currentPage, onPageChange } = usePagination();

  if (currentPage <= 1) return null;

  return (
    <StepControl
      {...props}
      defaultRel="prev"
      onSelect={() => onPageChange?.(currentPage - 1)}
      className={cn(
        STEP_VARIANTS({ layout }),
        "col-start-1 justify-self-start",
        PREVIOUS_CLASSES,
        className,
      )}
    >
      <ArrowLeft aria-hidden className={ICON_VARIANTS({ layout })} />
      {children}
    </StepControl>
  );
};

const STATUS_VARIANTS = cva(
  "col-start-2 shrink-0 whitespace-nowrap text-center tabular-nums text-fg-muted",
  {
    variants: { layout: { desktop: "text-body-sm", mobile: "text-body-xs" } },
  },
);

const Status: FC<HTMLAttributes<HTMLSpanElement>> = ({ className, children, ...props }) => {
  const { layout, currentPage, totalPages } = usePagination();

  return (
    <span className={cn(STATUS_VARIANTS({ layout }), className)} {...props}>
      {children ?? `${currentPage} / ${totalPages ?? "…"}`}
    </span>
  );
};

const Next: FC<StepProps> = ({ className, children, ...props }) => {
  const { layout, currentPage, totalPages, hasNextPage, onPageChange } = usePagination();

  const isLast = totalPages !== undefined ? currentPage >= totalPages : hasNextPage === false;
  if (isLast) return null;

  return (
    <StepControl
      {...props}
      defaultRel="next"
      onSelect={() => onPageChange?.(currentPage + 1)}
      className={cn(
        STEP_VARIANTS({ layout }),
        "col-start-3 justify-self-end",
        NEXT_CLASSES,
        className,
      )}
    >
      {children}
      <ArrowRight aria-hidden className={ICON_VARIANTS({ layout })} />
    </StepControl>
  );
};

Root.displayName = "Pagination";
Previous.displayName = "Pagination.Previous";
Status.displayName = "Pagination.Status";
Next.displayName = "Pagination.Next";

export const Pagination = Object.assign(Root, { Previous, Status, Next });
